import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../lib/authLib";
import { User, get as getUser } from "../lib/userLib";
import {
  Inbox,
  getStats,
  getHealth,
  getInboxWithOnboarding,
  InboxStats,
} from "../lib/inboxLib";
import "./dashboard.css";
import { Message, getPageForInbox } from "../lib/messagesLib";
import { useSnackbar } from "../contexts/SnackbarContext";
import {
  OnboardingData,
  OnboardingMilestoneType,
  MilestoneStatus,
  Milestone,
} from "../lib/types";
import {
  connectSocket,
  disconnectSocket,
  subscribeToEvent,
  unsubscribeFromEvent,
  EventNames,
  showSocketEventSnackbar,
} from "../lib/socketService";
import { useExperience } from "../hooks/useExperience";

import InboxCards from "./dashboard/InboxCards";
import MessagesCard from "./dashboard/MessagesCard";
import ActivityCard from "./dashboard/ActivityCard";
import FirstLoginModal from "./FirstLoginModal";
import ScheduledDeletionCanceledModal from "./ScheduledDeletionCanceledModal";
import ReauthWarning from "./ReauthWarning";
import ReauthModal from "./ReauthModal";
import { getTokenHealth } from "../lib/inboxLib";
import { ReauthResult } from "../lib/authLib";

interface DashboardProps {
  onMessageUpdate?: () => Promise<void>;
}

// Define the expected structure of milestone events
interface MilestoneEventPayload {
  inboxId: string;
  milestoneType: OnboardingMilestoneType;
  status: MilestoneStatus;
  timestamp: string;
  metadata?: {
    progressPercent?: number;
    messagesSynced?: number;
    totalToSync?: number;
    elapsedTime?: number;
    error?: string;
    details?: string;
  };
}

// Define other socket event payload types
interface MessageEventPayload {
  messageId: string;
  inboxId: string;
  [key: string]: unknown;
}

interface InboxEventPayload {
  inboxId: string;
  [key: string]: unknown;
}

interface HealthStatusEventPayload {
  inboxId: string;
  status: "healthy" | "unhealthy" | "loading" | "atRisk";
  message: string;
}

interface ChallengeEventPayload {
  challengeId: string;
  messageId: string;
  [key: string]: unknown;
}

const Dashboard: React.FC<DashboardProps> = ({ onMessageUpdate }) => {
  const { user: authUser, isAdmin } = useAuthContext();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingInbox, setEditingInbox] = useState<Inbox | null>(null);
  const [challengeMessage, setChallengeMessage] = useState("");
  const [bypassMessage, setBypassMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [stats, setStats] = useState<InboxStats | null>(null);
  const [healthStatuses, setHealthStatuses] = useState<
    Record<
      string,
      {
        status: "healthy" | "unhealthy" | "loading" | "atRisk";
        message: string;
      }
    >
  >({});
  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [inboxesWithOnboarding, setInboxesWithOnboarding] = useState<
    (Inbox & { onboarding: OnboardingData })[]
  >([]);
  const [selectedInboxId, setSelectedInboxId] = useState<string | "all">("all");
  const [currentInboxIdForMessages, setCurrentInboxIdForMessages] = useState<
    string | null
  >(null);
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);

  // Reauth modal state (triggered by ?reauth=:inboxId URL param)
  const [reauthModalOpen, setReauthModalOpen] = useState(false);
  const [reauthInboxId, setReauthInboxId] = useState<string | null>(null);
  const [reauthInboxEmail, setReauthInboxEmail] = useState<string>("");
  const [reauthReason, setReauthReason] = useState<
    "token_expiring" | "insufficient_scopes"
  >("token_expiring");

  // Refs to track refresh queue to avoid stacking up concurrent requests
  const isRefreshingDataRef = useRef(false);
  const needsRefreshRef = useRef(false);

  // Use the reusable experience hook for first-login
  const firstLoginExperience = useExperience({
    slug: "first-login",
    experiences: user?.experiences,
  });

  // Use the reusable experience hook for scheduled-deletion-canceled
  const scheduledDeletionCanceledExperience = useExperience({
    slug: "scheduled-deletion-canceled",
    experiences: user?.experiences,
  });

  // Get userId and period from query params or use defaults
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get("userId") || authUser?.id;
  const period = queryParams.get("period") || "1W";

  // Function to update period in URL and fetch new stats
  const updatePeriod = async (newPeriod: string) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set("period", newPeriod);
    navigate(`?${newParams.toString()}`);
  };

  // Function to fetch stats with period
  const fetchStats = useCallback(
    async (inboxId: string) => {
      try {
        const inboxStats = await getStats(inboxId, period);
        setStats(inboxStats);
      } catch (error) {
        console.error("Error fetching stats:", error);
        showSnackbar("Failed to load stats", "error");
      }
    },
    [period, showSnackbar]
  );

  // Function to fetch messages for a specific inbox
  const fetchMessages = useCallback(
    async (inboxId: string) => {
      try {
        const messagesData = await getPageForInbox(inboxId);
        setMessages(messagesData.messages);
      } catch (error) {
        console.error("Error fetching messages:", error);
        showSnackbar("Failed to load messages", "error");
      }
    },
    [showSnackbar]
  );

  // Function to refresh data after rule creation (same as websocket refresh)
  const refreshDataAfterRuleCreation = useCallback(async () => {
    if (currentInboxIdForMessages) {
      await fetchMessages(currentInboxIdForMessages);
      await fetchStats(currentInboxIdForMessages);
    }
  }, [currentInboxIdForMessages, fetchMessages, fetchStats]);

  // Queued refresh function that prevents concurrent requests
  const refreshDataQueued = useCallback(async () => {
    if (!currentInboxIdForMessages) return;

    if (isRefreshingDataRef.current) {
      // If already refreshing, just mark that we need another refresh
      needsRefreshRef.current = true;
      return;
    }

    // Start refreshing
    isRefreshingDataRef.current = true;
    needsRefreshRef.current = false;

    try {
      await fetchMessages(currentInboxIdForMessages);
      await fetchStats(currentInboxIdForMessages);
    } finally {
      // Done refreshing
      isRefreshingDataRef.current = false;

      // Check if we need to refresh again
      if (needsRefreshRef.current) {
        // Use requestAnimationFrame to avoid stack overflow
        requestAnimationFrame(async () => {
          await refreshDataQueued();
        });
      }
    }
  }, [currentInboxIdForMessages, fetchMessages, fetchStats]);

  // Function to fetch user data
  const fetchUserData = useCallback(async () => {
    if (!userId) return;
    try {
      const userData = await getUser(userId);
      setUser(userData);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [userId]);

  // Handle ?reauth=:inboxId URL parameter
  useEffect(() => {
    const reauthParam = queryParams.get("reauth");
    if (!reauthParam || !authUser) return;

    // Strip the param from URL immediately
    const newParams = new URLSearchParams(location.search);
    newParams.delete("reauth");
    const newSearch = newParams.toString();
    window.history.replaceState(
      {},
      "",
      `${location.pathname}${newSearch ? `?${newSearch}` : ""}`
    );

    // Check token health to determine if modal should open
    const checkAndOpenModal = async () => {
      try {
        const health = await getTokenHealth(reauthParam);
        if (health.needsReauth) {
          setReauthInboxId(reauthParam);
          setReauthInboxEmail(health.inboxEmail);
          setReauthReason(health.reason!);
          setReauthModalOpen(true);
        }
      } catch (error) {
        console.error("Failed to check token health for reauth:", error);
      }
    };

    checkAndOpenModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  const handleReauthSuccess = (result: ReauthResult) => {
    setReauthModalOpen(false);
    showSnackbar(`Gmail token refreshed for ${result.email}`, "success");
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setError("No user ID available");
        setLoading(false);
        return;
      }

      try {
        const userData = await getUser(userId);
        setUser(userData);

        // Fetch detailed inbox data with onboarding information
        if (userData.inboxes && userData.inboxes.length > 0) {
          const inboxesData = await Promise.all(
            userData.inboxes.map((inbox) => getInboxWithOnboarding(inbox.id))
          );
          setInboxesWithOnboarding(inboxesData);

          // Fetch stats and messages for the first inbox
          const firstInboxId = userData.inboxes[0].id;
          setCurrentInboxIdForMessages(firstInboxId);
          await fetchStats(firstInboxId);
          await fetchMessages(firstInboxId);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load user data");
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, period, fetchStats, fetchMessages]);

  useEffect(() => {
    const fetchHealthStatuses = async () => {
      if (!inboxesWithOnboarding.length) return;

      const statuses: Record<
        string,
        {
          status: "healthy" | "unhealthy" | "loading" | "atRisk";
          message: string;
        }
      > = {};
      // Set initial loading state for all inboxes
      inboxesWithOnboarding.forEach((inbox) => {
        statuses[inbox.id] = {
          status: "loading",
          message: "Checking health status...",
        };
      });
      setHealthStatuses(statuses);

      // Then fetch actual status
      for (const inbox of inboxesWithOnboarding) {
        try {
          const health = await getHealth(inbox.id);
          statuses[inbox.id] = health;
        } catch (error) {
          console.error(
            `Failed to fetch health status for inbox ${inbox.id}:`,
            error
          );
          statuses[inbox.id] = {
            status: "unhealthy",
            message: "Failed to fetch health status",
          };
        }
        // Update the status immediately after each inbox check
        setHealthStatuses((prevStatuses) => ({
          ...prevStatuses,
          [inbox.id]: statuses[inbox.id],
        }));
      }
    };

    // Only run the initial health check
    fetchHealthStatuses();

    // Set up interval for periodic health checks
    const interval = setInterval(fetchHealthStatuses, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [inboxesWithOnboarding]); // Depend on the entire array to refetch when it changes

  // Check if any inbox is currently onboarding
  useEffect(() => {
    const hasActiveOnboarding = inboxesWithOnboarding.some(
      (inbox) => inbox.onboarding.status === "in_progress"
    );
    setIsOnboardingActive(hasActiveOnboarding);
  }, [inboxesWithOnboarding]);

  // Set up periodic refresh during onboarding to ensure updates happen even if websocket events are throttled
  useEffect(() => {
    if (!currentInboxIdForMessages) return;
    const interval = setInterval(
      async () => {
        console.log(
          "Refreshing messages and stats for inbox",
          currentInboxIdForMessages
        );
        await refreshDataQueued();
      },
      5 * 60 * 1000
    ); // Refresh every 5 minutes

    return () => {
      console.log("Clearing periodic refresh interval");
      clearInterval(interval);
    };
  }, [isOnboardingActive, currentInboxIdForMessages, refreshDataQueued]);

  useEffect(() => {
    if (!userId) return;

    // Connect to socket
    connectSocket();

    // Handle page visibility changes to force updates when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && currentInboxIdForMessages) {
        console.log("Page became visible, forcing refresh");
        refreshDataQueued();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Subscribe to socket events
    const handleMessageReceived = (data: MessageEventPayload) => {
      showSocketEventSnackbar(EventNames.MESSAGE_RECEIVED, data);
      // Refresh messages and stats for the current inbox
      if (currentInboxIdForMessages) {
        refreshDataQueued();
      }
      // Also call the provided callback if it exists
      if (onMessageUpdate) {
        onMessageUpdate();
      }
    };

    const handleMessageUpdated = (data: MessageEventPayload) => {
      showSocketEventSnackbar(EventNames.MESSAGE_UPDATED, data);
      // Refresh messages and stats for the current inbox
      if (currentInboxIdForMessages) {
        refreshDataQueued();
      }
      // Also call the provided callback if it exists
      if (onMessageUpdate) {
        onMessageUpdate();
      }
    };

    const handleInboxUpdated = (data: InboxEventPayload) => {
      showSocketEventSnackbar(EventNames.INBOX_UPDATED, data);
      // Refresh inbox data if needed
      fetchUserData();
    };

    const handleHealthStatusUpdated = (data: HealthStatusEventPayload) => {
      showSocketEventSnackbar(EventNames.HEALTH_STATUS_UPDATED, data);
      // Update health status
      setHealthStatuses((prev) => ({
        ...prev,
        [data.inboxId]: {
          status: data.status,
          message: data.message,
        },
      }));
    };

    const handleChallengeCreated = (data: ChallengeEventPayload) => {
      showSocketEventSnackbar(EventNames.CHALLENGE_CREATED, data);
      // Refresh messages and stats for the current inbox
      if (currentInboxIdForMessages) {
        refreshDataQueued();
      }
      // Also call the provided callback if it exists
      if (onMessageUpdate) {
        onMessageUpdate();
      }
    };

    const handleChallengeUpdated = (data: ChallengeEventPayload) => {
      showSocketEventSnackbar(EventNames.CHALLENGE_UPDATED, data);
      // Refresh messages and stats for the current inbox
      if (currentInboxIdForMessages) {
        refreshDataQueued();
      }
      // Also call the provided callback if it exists
      if (onMessageUpdate) {
        onMessageUpdate();
      }
    };

    // Create stable event handlers that won't change between renders
    const handleMilestoneStarted = (data: MilestoneEventPayload) => {
      handleMilestoneEvent({
        ...data,
        eventType: EventNames.ONBOARDING_MILESTONE_STARTED,
      });
    };

    const handleMilestoneCompleted = (data: MilestoneEventPayload) => {
      handleMilestoneEvent({
        ...data,
        eventType: EventNames.ONBOARDING_MILESTONE_COMPLETED,
      });
    };

    const handleMilestoneProgress = (data: MilestoneEventPayload) => {
      console.log(`Milestone progress received:`, data);
      handleMilestoneEvent({
        ...data,
        eventType: EventNames.ONBOARDING_MILESTONE_PROGRESS,
      });
    };

    // New consolidated milestone event handler with full refetch
    const handleMilestoneEvent = async (
      data: MilestoneEventPayload & { eventType: EventNames }
    ) => {
      // Show immediate feedback via snackbar
      showSocketEventSnackbar(data.eventType, {
        milestone: data.milestoneType,
        status: data.status,
        progressPercent: data.metadata?.progressPercent,
        ...(data.metadata || {}),
      });

      // Guard against undefined inbox ID
      if (!data.inboxId) {
        console.error("Missing inbox ID in milestone event:", data);
        showSnackbar(
          "Failed to update inbox status: Missing inbox ID",
          "error"
        );
        return;
      }

      try {
        // For progress updates, just update the milestone progress in the state
        if (data.eventType === EventNames.ONBOARDING_MILESTONE_PROGRESS) {
          setInboxesWithOnboarding((prevInboxes) =>
            prevInboxes.map((inbox) => {
              if (inbox.id === data.inboxId) {
                return {
                  ...inbox,
                  onboarding: {
                    ...inbox.onboarding,
                    milestones: inbox.onboarding.milestones.map(
                      (milestone: Milestone) =>
                        milestone.type === data.milestoneType
                          ? {
                              ...milestone,
                              metadata: {
                                ...milestone.metadata,
                                progressPercent: data.metadata?.progressPercent,
                                messagesSynced: data.metadata?.messagesSynced,
                                totalToSync: data.metadata?.totalToSync,
                                elapsedTime: data.metadata?.elapsedTime,
                              },
                            }
                          : milestone
                    ),
                  },
                };
              }
              return inbox;
            })
          );

          // Refresh messages and stats for all milestone progress events if this is for the current inbox
          if (data.inboxId === currentInboxIdForMessages) {
            const progressInfo = `${data.metadata?.progressPercent}%`;
            console.log(
              `Refreshing data for inbox ${data.inboxId} due to ${data.milestoneType} progress update (${progressInfo})`
            );

            // Use requestAnimationFrame to ensure updates happen even when page doesn't have focus
            requestAnimationFrame(async () => {
              await refreshDataQueued();
            });
          }

          return;
        }

        // For other milestone events, refetch the complete inbox state
        console.log("Refetching inbox state for milestone event:", data);
        const updatedInbox = await getInboxWithOnboarding(data.inboxId);

        // Update the inboxes state with the fresh data
        setInboxesWithOnboarding((prevInboxes) =>
          prevInboxes.map((inbox) =>
            inbox.id === data.inboxId ? updatedInbox : inbox
          )
        );

        // If this is a completion event, also refresh the health status
        if (data.eventType === EventNames.ONBOARDING_MILESTONE_COMPLETED) {
          const health = await getHealth(data.inboxId);
          setHealthStatuses((prev) => ({
            ...prev,
            [data.inboxId]: health,
          }));
        }

        // Refresh messages and stats if this is for the current inbox
        if (data.inboxId === currentInboxIdForMessages) {
          console.log(
            `Refreshing messages and stats for inbox ${data.inboxId} due to milestone event: ${data.milestoneType}`
          );
          await refreshDataQueued();
        }

        // Log successful update
        console.debug("Successfully updated inbox state for milestone event:", {
          inboxId: data.inboxId,
          milestone: data.milestoneType,
          status: data.status,
          progressPercent: data.metadata?.progressPercent,
          timestamp: data.timestamp,
        });
      } catch (error) {
        console.error("Failed to refresh inbox state:", error);
        showSnackbar("Failed to update inbox status", "error");
      }
    };

    // Subscribe to all events
    subscribeToEvent(EventNames.MESSAGE_RECEIVED, handleMessageReceived);
    subscribeToEvent(EventNames.MESSAGE_UPDATED, handleMessageUpdated);
    subscribeToEvent(EventNames.INBOX_UPDATED, handleInboxUpdated);
    subscribeToEvent(
      EventNames.HEALTH_STATUS_UPDATED,
      handleHealthStatusUpdated
    );
    subscribeToEvent(EventNames.CHALLENGE_CREATED, handleChallengeCreated);
    subscribeToEvent(EventNames.CHALLENGE_UPDATED, handleChallengeUpdated);
    subscribeToEvent(
      EventNames.ONBOARDING_MILESTONE_STARTED,
      handleMilestoneStarted
    );
    subscribeToEvent(
      EventNames.ONBOARDING_MILESTONE_COMPLETED,
      handleMilestoneCompleted
    );
    subscribeToEvent(
      EventNames.ONBOARDING_MILESTONE_PROGRESS,
      handleMilestoneProgress
    );

    // Cleanup function
    return () => {
      if (userId) {
        disconnectSocket();
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      unsubscribeFromEvent(EventNames.MESSAGE_RECEIVED, handleMessageReceived);
      unsubscribeFromEvent(EventNames.MESSAGE_UPDATED, handleMessageUpdated);
      unsubscribeFromEvent(EventNames.INBOX_UPDATED, handleInboxUpdated);
      unsubscribeFromEvent(
        EventNames.HEALTH_STATUS_UPDATED,
        handleHealthStatusUpdated
      );
      unsubscribeFromEvent(
        EventNames.CHALLENGE_CREATED,
        handleChallengeCreated
      );
      unsubscribeFromEvent(
        EventNames.CHALLENGE_UPDATED,
        handleChallengeUpdated
      );
      unsubscribeFromEvent(
        EventNames.ONBOARDING_MILESTONE_STARTED,
        handleMilestoneStarted
      );
      unsubscribeFromEvent(
        EventNames.ONBOARDING_MILESTONE_COMPLETED,
        handleMilestoneCompleted
      );
      unsubscribeFromEvent(
        EventNames.ONBOARDING_MILESTONE_PROGRESS,
        handleMilestoneProgress
      );
    };
  }, [
    userId,
    onMessageUpdate,
    currentInboxIdForMessages,
    refreshDataQueued,
    fetchUserData,
    showSnackbar,
  ]);

  const saveInboxMessages = async () => {
    if (!editingInbox) return;

    // In a real implementation, you would update the inbox messages via API
    console.log(`Updating messages for inbox ${editingInbox.id}`);
    console.log(`Challenge message: ${challengeMessage}`);
    console.log(`Bypass message: ${bypassMessage}`);

    // Optimistic UI update
    if (user) {
      const updatedInboxes = user.inboxes.map((i: Inbox) =>
        i.id === editingInbox.id ? { ...i, challengeMessage, bypassMessage } : i
      );
      setUser({ ...user, inboxes: updatedInboxes });
    }

    setEditingInbox(null);
  };

  if (loading) return <div className="text-center p-8">Loading...</div>;
  if (error) return <div className="text-center p-8 text-red-500">{error}</div>;
  if (!user)
    return <div className="text-center p-8">No user data available</div>;

  return (
    <div className="container mx-auto py-6 px-4">
      {/* Show reauth warning for primary inbox (token expiring or scope issues) */}
      {user.inboxes?.[0] && <ReauthWarning inboxId={user.inboxes[0].id} />}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Welcome, {user.firstName}</h1>
        <div className="flex items-center">
          <span className="mr-3 text-sm text-gray-600 dark:text-gray-300">
            Stats for:
          </span>
          <div className="flex border border-gray-300 dark:border-gray-600 rounded-full overflow-hidden bg-gray-50 dark:bg-gray-800">
            <button
              className={`px-3 py-1 text-sm transition-colors focus:outline-none ${
                selectedInboxId === "all"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
              } rounded-l-full`}
              onClick={() => setSelectedInboxId("all")}
            >
              All inboxes
            </button>
            {user.inboxes?.map((inbox, idx) => (
              <button
                key={inbox.id}
                className={`px-3 py-1 text-sm transition-colors focus:outline-none ${
                  selectedInboxId === inbox.id
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
                } ${idx === user.inboxes.length - 1 ? "rounded-r-full" : ""}`}
                onClick={() => setSelectedInboxId(inbox.id)}
              >
                {inbox.displayName || inbox.email}
              </button>
            ))}
          </div>
          <button
            className="ml-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 text-xl"
            onClick={() => {
              /* TODO: Add inbox logic */
            }}
            aria-label="Add inbox"
          >
            +
          </button>
        </div>
      </div>

      {/* Main layout container */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left column: Inbox cards (A) above Messages (B) on large screens */}
        <div className="order-1 lg:order-none lg:w-[40%] flex flex-col gap-6">
          <InboxCards
            inboxesWithOnboarding={inboxesWithOnboarding}
            healthStatuses={healthStatuses}
            setHealthStatuses={setHealthStatuses}
            userId={userId}
            isAdmin={isAdmin}
          />
          <div className="lg:block hidden">
            <MessagesCard
              messages={messages}
              onRuleCreated={refreshDataAfterRuleCreation}
              showSnackbar={showSnackbar}
            />
          </div>
        </div>

        {/* Right column: Activity Card (C) */}
        <div className="order-2 lg:order-none lg:w-[60%]">
          <ActivityCard
            stats={stats}
            period={period}
            updatePeriod={updatePeriod}
          />
        </div>

        {/* Messages Card for small screens */}
        <div className="order-3 lg:hidden">
          <MessagesCard
            messages={messages}
            onRuleCreated={refreshDataAfterRuleCreation}
            showSnackbar={showSnackbar}
          />
        </div>
      </div>

      {/* First Login Modal */}
      {firstLoginExperience.shouldShow && (
        <FirstLoginModal
          onClose={firstLoginExperience.complete}
          onShow={() => updatePeriod("all")}
        />
      )}

      {/* Scheduled Deletion Canceled Modal */}
      {scheduledDeletionCanceledExperience.shouldShow && (
        <ScheduledDeletionCanceledModal
          onClose={scheduledDeletionCanceledExperience.complete}
        />
      )}

      {/* Reauth Modal (triggered by URL param) */}
      {reauthModalOpen && reauthInboxId && (
        <ReauthModal
          isOpen={reauthModalOpen}
          onClose={() => setReauthModalOpen(false)}
          onSuccess={handleReauthSuccess}
          inboxId={reauthInboxId}
          inboxEmail={reauthInboxEmail}
          reason={reauthReason}
        />
      )}

      {/* Edit Modal */}
      {editingInbox && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setEditingInbox(null);
            }
          }}
          tabIndex={0}
        >
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4">Edit Inbox Messages</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Challenge Message
              </label>
              <textarea
                value={challengeMessage}
                onChange={(e) => setChallengeMessage(e.target.value)}
                className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                rows={3}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bypass Message
              </label>
              <textarea
                value={bypassMessage}
                onChange={(e) => setBypassMessage(e.target.value)}
                className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setEditingInbox(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={saveInboxMessages}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
